/**
 * The LinkedIn document title — and why it needs a default.
 *
 * `validateLinkedinDocumentTitleExists` refuses to publish a LinkedIn post that
 * carries a PDF without a title, so an untouched upload would otherwise stop the
 * user at the publish button over a field they never saw. The default fills it
 * the moment a document lands in the post.
 *
 * The distinction that makes this safe to run on every render is `null` vs `''`:
 * `null` is the slice's initial value and means nobody has typed here yet, while
 * an empty string is a deliberate clear and stays empty — so the validator can
 * still complain about it, and the default never grows back under the cursor.
 */
import {
  LINKEDIN_BUSINESS_PAGE,
  LINKEDIN_PERSONAL_ACCOUNT
} from '../../../../config/constants';
import { setPostDataAttribute } from '../../../../redux/slices/lomavisCreator/lomavisCreator';
import { MEDIA_TYPE_DOC } from '../../config/constants';

export const LINKEDIN_DOCUMENT_TITLE_ATTRIBUTE = 'linkedin_document_title';

export const LINKEDIN_DOCUMENT_TITLE_DEFAULT_KEY =
  'lomavisCreator.additionalOptions.linkedin_document_title_default';

/**
 * The two platforms that share the field.
 *
 * A *typed* title goes to both: LinkedIn keys the title to the document rather
 * than to the post, so the same PDF cannot carry two of them, which is what the
 * field's helper text promises the user. The default is deliberately narrower —
 * see `applyLinkedinDocumentTitleDefault`.
 */
const LINKEDIN_PLATFORMS = [LINKEDIN_PERSONAL_ACCOUNT, LINKEDIN_BUSINESS_PAGE];

export const postHasDocumentMedia = (media: any): boolean =>
  Array.isArray(media) &&
  media.some((item: any) => item?.media_type === MEDIA_TYPE_DOC);

export const needsLinkedinDocumentTitleDefault = (
  media: any,
  title: unknown
): boolean => title == null && postHasDocumentMedia(media);

const writeTitle = (dispatch: any, platform: string, value: string): void => {
  dispatch(
    setPostDataAttribute({
      platform,
      attribute: LINKEDIN_DOCUMENT_TITLE_ATTRIBUTE,
      value
    })
  );
};

/** A title the user typed applies to the document, so to both platforms. */
export const setLinkedinDocumentTitle = (dispatch: any, value: string): void => {
  LINKEDIN_PLATFORMS.forEach((platform) => {
    writeTitle(dispatch, platform, value);
  });
};

/**
 * The LinkedIn platforms whose *own* title is still untouched beside a document.
 *
 * Broadcasting the default the way a typed title is broadcast would destroy
 * data: with individual media the two platforms can legitimately hold different
 * documents, and a loaded draft can have a custom business-page title while the
 * personal-account title is still null. Filling the untitled one would then
 * overwrite the custom one with "My Slideshow". So the default is written per
 * platform, only where it is actually missing.
 */
export const linkedinPlatformsNeedingDocumentTitleDefault = (
  postData: any
): string[] =>
  LINKEDIN_PLATFORMS.filter((platform) =>
    needsLinkedinDocumentTitleDefault(
      postData?.[platform]?.media,
      postData?.[platform]?.[LINKEDIN_DOCUMENT_TITLE_ATTRIBUTE]
    )
  );

export const applyLinkedinDocumentTitleDefault = (
  dispatch: any,
  postData: any,
  value: string
): void => {
  linkedinPlatformsNeedingDocumentTitleDefault(postData).forEach((platform) => {
    writeTitle(dispatch, platform, value);
  });
};
